import { Fragment, useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * /admin/journey — the RDJ QC / transparency console (Workstream E).
 *
 * The ratified doctrine: every automated touch must be able to explain
 * itself. This page reads /api/admin/journey-qc and shows, for any
 * prospect or for the whole system, the full "why then / why not" story:
 *
 *   Sends        — every journey send with its stamped reason JSON,
 *                  skip_reason, cap/meeting deferral breadcrumbs, attempts
 *                  and errors. Click a row to expand the raw rationale.
 *   Enrollments  — who is in which sequence, status, test flag, compression.
 *   Meetings held— held facts by source with the winning verdict computed
 *                  under the ratified precedence (zoom > fathom > mart >
 *                  manual; a higher-precedence held=false beats a lower
 *                  held=true).
 *   Magic links  — request outcomes (sent / no_account / no_portal /
 *                  rate_limited) with TTL and consumption stamps.
 *   Messages     — Decision Center thread activity, both directions.
 *   Suppressions — the do-not-email list, verbatim.
 *
 * Read-only by design: the console observes the machine, it never steers
 * it. Auth mirrors the other /admin pages (portal session + isAdmin).
 */

type QcPayload = {
  ok: boolean;
  error?: string;
  message?: string;
  scope: string | null;
  profileEmails: Record<string, string>;
  sends: any[];
  enrollments: any[];
  held: any[];
  magicLinks: any[];
  messages: any[];
  suppressions: any[];
  generatedAt?: string;
};

type Section = 'sends' | 'enrollments' | 'held' | 'magic' | 'messages' | 'suppressions';

type TimelineEvent = {
  t: string;
  kind: string;
  title: string;
  detail?: string | null;
  held?: boolean;
  sendId?: string;
  status?: string;
  hasBody?: boolean;
  direction?: string;
  body?: string;
};

type TimelineData = {
  ok: boolean;
  error?: string;
  profile: { profileId: string; name: string; email: string; advisor: string | null };
  events: TimelineEvent[];
};

const ORD = ['1st', '2nd', '3rd', '4th', '5th', '6th'];

const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: 'sends', label: 'Sends' },
  { id: 'enrollments', label: 'Enrollments' },
  { id: 'held', label: 'Meetings held' },
  { id: 'magic', label: 'Magic links' },
  { id: 'messages', label: 'Messages' },
  { id: 'suppressions', label: 'Suppressions' },
];

const HELD_PRECEDENCE = ['zoom', 'fathom', 'mart', 'manual'];

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  }).format(new Date(iso));
}

function statusBadge(status: string, skipReason: string | null): string {
  if (status === 'sent') return 'sent';
  if (status === 'pending' && skipReason?.startsWith('deferred')) return 'deferred';
  return status;
}

/** Winning held verdict per booking under the ratified precedence. */
function heldVerdicts(rows: any[]): Record<string, string> {
  const best: Record<string, any> = {};
  for (const r of rows) {
    const prev = best[r.booking_id];
    if (!prev || HELD_PRECEDENCE.indexOf(r.source) < HELD_PRECEDENCE.indexOf(prev.source)) {
      best[r.booking_id] = r;
    }
  }
  const out: Record<string, string> = {};
  for (const [bid, r] of Object.entries(best)) {
    out[bid] = `${r.held ? 'HELD' : 'NOT HELD'} via ${(r as any).source}`;
  }
  return out;
}

export function AdminJourneyQc() {
  const auth = useCurrentUser();

  const [data, setData] = useState<QcPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [section, setSection] = useState<Section>('sends');
  const [emailInput, setEmailInput] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [profileEmail, setProfileEmail] = useState<string | null>(() => {
    // The audit view deep-links here with ?email=<prospect> ("full timeline").
    try {
      const e = new URLSearchParams(window.location.search).get('email');
      return e ? e.trim().toLowerCase() : null;
    } catch {
      return null;
    }
  });
  const [timeline, setTimeline] = useState<TimelineData | null>(null);
  const [tlErr, setTlErr] = useState<string | null>(null);
  const [openSend, setOpenSend] = useState<string | null>(null);
  const [sendCache, setSendCache] = useState<Record<string, any>>({});

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [auth.status]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function fetchData() {
      try {
        const url = new URL('/api/admin/journey-qc', window.location.origin);
        if (emailFilter) url.searchParams.set('email', emailFilter);
        const res = await authedFetch(url.toString(), { cache: 'no-store' });
        const json = (await res.json()) as QcPayload;
        if (cancelled) return;
        if (!res.ok || !json.ok) {
          setErr(json.message ?? json.error ?? `HTTP ${res.status}`);
        } else {
          setErr(null);
          setData(json);
          setLastUpdated(new Date());
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    fetchData();
    const timer = window.setInterval(fetchData, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [auth, emailFilter]);

  // QC v2 — prospect timeline (Zach 9/9)
  useEffect(() => {
    if (!profileEmail || auth.status !== 'authenticated' || !auth.user.isAdmin) return;
    let cancelled = false;
    setTimeline(null);
    setTlErr(null);
    setOpenSend(null);
    (async () => {
      try {
        const url = new URL('/api/admin/journey-qc', window.location.origin);
        url.searchParams.set('email', profileEmail);
        url.searchParams.set('view', 'timeline');
        const res = await authedFetch(url.toString(), { cache: 'no-store' });
        const json = (await res.json()) as TimelineData;
        if (cancelled) return;
        if (!res.ok || !json.ok) setTlErr(json.error ?? `HTTP ${res.status}`);
        else setTimeline(json);
      } catch (err2) {
        if (!cancelled) setTlErr(err2 instanceof Error ? err2.message : 'Network error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileEmail, auth]);

  async function toggleSend(sendId: string) {
    if (openSend === sendId) {
      setOpenSend(null);
      return;
    }
    setOpenSend(sendId);
    if (!sendCache[sendId]) {
      try {
        const url = new URL('/api/admin/journey-qc', window.location.origin);
        url.searchParams.set('send', sendId);
        const res = await authedFetch(url.toString(), { cache: 'no-store' });
        const json = await res.json();
        if (json.ok) setSendCache((c) => ({ ...c, [sendId]: json.send }));
        else setSendCache((c) => ({ ...c, [sendId]: { error: json.error ?? 'load failed' } }));
      } catch {
        setSendCache((c) => ({ ...c, [sendId]: { error: 'load failed' } }));
      }
    }
  }

  const emailOf = (profileId: string | null, fallback?: string | null): string =>
    fallback || (profileId ? data?.profileEmails[profileId] ?? '—' : '—');

  const verdicts = data ? heldVerdicts(data.held) : {};

  const profCell = (em: string) =>
    em && em !== '—' ? (
      <button type="button" className="aqc-link" onClick={() => setProfileEmail(em)}>
        {em}
      </button>
    ) : (
      em
    );

  function renderProfile() {
    if (tlErr)
      return (
        <div className="admin-err" role="alert">
          <strong>Error:</strong> {tlErr}
        </div>
      );
    if (!timeline) return <p className="admin-empty">Loading journey…</p>;
    const milestones = timeline.events
      .filter((e) => e.kind === 'held' && e.held)
      .map((e) => new Date(e.t).getTime());
    let lastPhase = -1;
    return (
      <div className="aqc-profile">
        <div className="aqc-prof-head">
          <div>
            <div className="aqc-prof-name">{timeline.profile.name}</div>
            <div className="aqc-prof-meta">
              {timeline.profile.email}
              {timeline.profile.advisor ? ` · advisor: ${timeline.profile.advisor}` : ''}
            </div>
          </div>
          <button type="button" className="admin-filter-pill" onClick={() => setProfileEmail(null)}>
            ← Back to lists
          </button>
        </div>
        <div className="aqc-tl">
          {timeline.events.length === 0 && (
            <p className="admin-empty">No journey activity for this prospect yet.</p>
          )}
          {timeline.events.map((e, i) => {
            const phase = milestones.filter((m) => m < new Date(e.t).getTime()).length;
            const header =
              phase !== lastPhase && phase > 0 ? (
                <div className="aqc-phase">After the {ORD[phase - 1] ?? `${phase}th`} meeting</div>
              ) : null;
            lastPhase = phase;
            const detail = openSend && e.sendId === openSend ? sendCache[openSend] : null;
            return (
              <Fragment key={`${e.kind}-${i}`}>
                {header}
                <div className={`aqc-ev aqc-${e.kind}${e.kind === 'held' && !e.held ? ' aqc-notheld' : ''}`}>
                  <div className="aqc-dot" />
                  <div className="aqc-body">
                    <div className="aqc-when">{fmtTs(e.t)}</div>
                    <div className="aqc-title">
                      {e.title}
                      {e.kind === 'send' && (
                        <button
                          type="button"
                          className="aqc-link"
                          style={{ marginLeft: 8 }}
                          onClick={() => toggleSend(e.sendId!)}
                        >
                          {openSend === e.sendId ? 'hide email ▴' : 'view email ▾'}
                        </button>
                      )}
                    </div>
                    {e.detail && <div className="aqc-detail">{e.detail}</div>}
                    {e.kind === 'message' && e.body && (
                      <div className={`aqc-msg ${e.direction === 'team' ? 'team' : 'mine'}`}>{e.body}</div>
                    )}
                    {e.kind === 'send' && openSend === e.sendId && (
                      <div className="aqc-mailwrap">
                        {!detail && <div className="aqc-detail">Loading email…</div>}
                        {detail?.error && <div className="admin-err">{detail.error}</div>}
                        {detail && !detail.error && detail.body_html && (
                          <iframe
                            className="aqc-mail"
                            sandbox=""
                            srcDoc={detail.body_html}
                            title={detail.subject ?? 'email'}
                          />
                        )}
                        {detail && !detail.error && !detail.body_html && (
                          <div className="aqc-detail">
                            {detail.body_text ? (
                              <pre className="aqc-pre">{detail.body_text}</pre>
                            ) : detail.status === 'sent' ? (
                              'Body not captured — this send predates body storage (0091). Newer sends show the exact email.'
                            ) : (
                              'Not sent yet — no body exists until the moment of delivery.'
                            )}
                            {detail.reason && (
                              <pre className="aqc-pre">{JSON.stringify(detail.reason, null, 2)}</pre>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-app">
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1>Journey QC</h1>
            <p className="admin-sub">
              Why every journey touch did — or didn't — happen. Refreshes every 60s
              {lastUpdated && (
                <>
                  {' · last updated '}
                  <strong>{fmtTs(lastUpdated.toISOString())}</strong>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="admin-filters">
          <div className="admin-filter-pills" role="tablist" aria-label="QC section">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                className={'admin-filter-pill ' + (section === s.id ? 'admin-filter-pill-active' : '')}
                onClick={() => {
                  setSection(s.id);
                  setExpanded(null);
                }}
                aria-selected={section === s.id}
              >
                {s.label}
                {data && (
                  <>
                    {' '}
                    (
                    {s.id === 'sends'
                      ? data.sends.length
                      : s.id === 'enrollments'
                      ? data.enrollments.length
                      : s.id === 'held'
                      ? data.held.length
                      : s.id === 'magic'
                      ? data.magicLinks.length
                      : s.id === 'messages'
                      ? data.messages.length
                      : data.suppressions.length}
                    )
                  </>
                )}
              </button>
            ))}
          </div>
          <form
            style={{ display: 'flex', gap: 8, marginTop: 10 }}
            onSubmit={(e) => {
              e.preventDefault();
              setEmailFilter(emailInput.trim().toLowerCase());
            }}
          >
            <input
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Filter by prospect email…"
              style={{
                flex: '0 1 320px',
                padding: '8px 10px',
                border: '1px solid #c4cbd4',
                borderRadius: 6,
                font: 'inherit',
                fontSize: 13,
              }}
            />
            <button type="submit" className="admin-filter-pill">
              Search
            </button>
            {emailFilter && (
              <button
                type="button"
                className="admin-filter-pill"
                onClick={() => {
                  setEmailInput('');
                  setEmailFilter('');
                }}
              >
                Clear ({emailFilter})
              </button>
            )}
          </form>
        </div>
        <nav className="admin-nav">
          <a href="/admin/journey/audit" className="admin-nav-link">
            Audit
          </a>
          <a href="/admin/routing" className="admin-nav-link">
            Routing Log
          </a>
          <a href="/admin/advisors" className="admin-nav-link">
            Advisors
          </a>
          <a href="/admin/availability" className="admin-nav-link">
            Advisor Availability
          </a>
          <a href="/admin/reminder-opt-outs" className="admin-nav-link">
            Reminder Opt-Outs
          </a>
        </nav>
      </header>

      <style>{`
        .aqc-link { background: none; border: none; padding: 0; font: inherit; color: #1B3A5C; text-decoration: underline; cursor: pointer; }
        .aqc-link:hover { color: #C41E3A; }
        .aqc-profile { text-align: left; }
        .aqc-prof-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
        .aqc-prof-name { font-size: 20px; font-weight: 600; }
        .aqc-prof-meta { font-size: 13px; color: #667; margin-top: 2px; }
        .aqc-phase { font-weight: 700; font-size: 13px; letter-spacing: 0.4px; text-transform: uppercase; color: #1B3A5C; border-top: 2px solid #1B3A5C; padding-top: 10px; margin: 22px 0 8px; }
        .aqc-tl { border-left: 3px solid #d6dbe2; padding-left: 18px; margin-left: 6px; }
        .aqc-ev { position: relative; padding: 8px 0; }
        .aqc-dot { position: absolute; left: -25px; top: 14px; width: 11px; height: 11px; border-radius: 50%; background: #d6dbe2; border: 2px solid #fff; }
        .aqc-held .aqc-dot { background: #1B3A5C; width: 15px; height: 15px; left: -27px; }
        .aqc-notheld .aqc-dot { background: #C41E3A; }
        .aqc-held .aqc-title { font-size: 16px; font-weight: 700; color: #1B3A5C; }
        .aqc-notheld .aqc-title { color: #C41E3A; }
        .aqc-message .aqc-dot { background: #C41E3A; }
        .aqc-when { font-size: 11px; color: #889; }
        .aqc-title { font-size: 14px; font-weight: 600; margin-top: 1px; }
        .aqc-detail { font-size: 12px; color: #556; margin-top: 2px; }
        .aqc-msg { margin-top: 6px; font-size: 13px; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere; background: #f6f8fa; border-left: 3px solid #d6dbe2; border-radius: 6px; padding: 10px 12px; max-width: 640px; }
        .aqc-msg.team { border-left-color: #C41E3A; }
        .aqc-mailwrap { margin-top: 8px; }
        .aqc-mail { width: 100%; max-width: 720px; height: 560px; border: 1px solid #d6dbe2; border-radius: 8px; background: #fff; }
        .aqc-pre { margin: 6px 0 0; font-size: 12px; white-space: pre-wrap; overflow-wrap: anywhere; background: #f6f8fa; border-radius: 6px; padding: 10px; max-width: 720px; }
      `}</style>

      <main className="admin-main">
        {profileEmail ? (
          renderProfile()
        ) : (
          <>
        {err && (
          <div className="admin-err" role="alert">
            <strong>Error:</strong> {err}
          </div>
        )}
        {loading && !data && !err && <p className="admin-empty">Loading…</p>}

        {data && section === 'sends' && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Scheduled</th>
                  <th>Template</th>
                  <th>Recipient</th>
                  <th>Sequence</th>
                  <th>Status</th>
                  <th>Why not / deferral</th>
                  <th>Sent</th>
                  <th>Test</th>
                </tr>
              </thead>
              <tbody>
                {data.sends.length === 0 && (
                  <tr>
                    <td colSpan={8} className="admin-empty">
                      No sends in scope.
                    </td>
                  </tr>
                )}
                {data.sends.map((s) => {
                  const en: any = s.journey_enrollments;
                  const seq = en?.journey_sequences?.slug ?? '—';
                  const isOpen = expanded === s.send_id;
                  return (
                    <Fragment key={s.send_id}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : s.send_id)}
                        style={{ cursor: 'pointer' }}
                        title="Click to expand the stamped rationale"
                      >
                        <td>{fmtTs(s.scheduled_for)}</td>
                        <td>{s.template_key}</td>
                        <td>{profCell(emailOf(s.profile_id, s.recipient_email))}</td>
                        <td>{seq}</td>
                        <td>
                          <strong>{statusBadge(s.status, s.skip_reason)}</strong>
                        </td>
                        <td>{s.skip_reason ?? (s.error ? `error: ${s.error.slice(0, 60)}` : '—')}</td>
                        <td>{fmtTs(s.sent_at)}</td>
                        <td>{en?.is_test ? `yes ×${en.time_compression || 1}` : '—'}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8}>
                            <pre
                              style={{
                                margin: 0,
                                padding: 12,
                                background: '#f6f8fa',
                                borderRadius: 6,
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {JSON.stringify(
                                {
                                  send_id: s.send_id,
                                  subject: s.subject,
                                  attempts: s.attempts,
                                  error: s.error,
                                  enrollment_status: en?.status,
                                  reason: s.reason,
                                },
                                null,
                                2
                              )}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && section === 'enrollments' && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Enrolled</th>
                  <th>Prospect</th>
                  <th>Sequence</th>
                  <th>Trigger</th>
                  <th>Seq status</th>
                  <th>Enrollment</th>
                  <th>Test</th>
                </tr>
              </thead>
              <tbody>
                {data.enrollments.length === 0 && (
                  <tr>
                    <td colSpan={7} className="admin-empty">
                      No enrollments in scope.
                    </td>
                  </tr>
                )}
                {data.enrollments.map((en) => {
                  const seq: any = en.journey_sequences;
                  return (
                    <tr key={en.enrollment_id}>
                      <td>{fmtTs(en.enrolled_at)}</td>
                      <td>{profCell(emailOf(en.profile_id))}</td>
                      <td>{seq?.slug ?? '—'}</td>
                      <td>{seq?.trigger_event ?? '—'}</td>
                      <td>{seq?.status ?? '—'}</td>
                      <td>
                        <strong>{en.status}</strong>
                      </td>
                      <td>{en.is_test ? `yes ×${en.time_compression || 1}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && section === 'held' && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Recorded</th>
                  <th>Prospect</th>
                  <th>Booking</th>
                  <th>Source</th>
                  <th>Held?</th>
                  <th>Duration</th>
                  <th>Winning verdict</th>
                </tr>
              </thead>
              <tbody>
                {data.held.length === 0 && (
                  <tr>
                    <td colSpan={7} className="admin-empty">
                      No held facts in scope.
                    </td>
                  </tr>
                )}
                {data.held.map((h) => (
                  <tr key={`${h.booking_id}-${h.source}`}>
                    <td>{fmtTs(h.occurred_at ?? h.created_at)}</td>
                    <td>{profCell(emailOf(h.profile_id))}</td>
                    <td>{String(h.booking_id).slice(0, 8)}…</td>
                    <td>{h.source}</td>
                    <td>{h.held ? 'yes' : 'no'}</td>
                    <td>{h.duration_minutes ? `${h.duration_minutes}m` : '—'}</td>
                    <td>
                      <strong>{verdicts[h.booking_id]}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && section === 'magic' && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>Email</th>
                  <th>Outcome</th>
                  <th>Expires</th>
                  <th>Used</th>
                </tr>
              </thead>
              <tbody>
                {data.magicLinks.length === 0 && (
                  <tr>
                    <td colSpan={5} className="admin-empty">
                      No magic-link requests in scope.
                    </td>
                  </tr>
                )}
                {data.magicLinks.map((m) => (
                  <tr key={m.magic_link_id}>
                    <td>{fmtTs(m.created_at)}</td>
                    <td>{profCell(m.email_normalized)}</td>
                    <td>
                      <strong>{m.outcome}</strong>
                    </td>
                    <td>{fmtTs(m.expires_at)}</td>
                    <td>{fmtTs(m.used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && section === 'messages' && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Prospect</th>
                  <th>Direction</th>
                  <th>Author</th>
                  <th>Body</th>
                  <th>Status</th>
                  <th>Read</th>
                </tr>
              </thead>
              <tbody>
                {data.messages.length === 0 && (
                  <tr>
                    <td colSpan={7} className="admin-empty">
                      No portal messages in scope.
                    </td>
                  </tr>
                )}
                {data.messages.map((m) => (
                  <tr key={m.message_id}>
                    <td>{fmtTs(m.created_at)}</td>
                    <td>{profCell(emailOf(m.profile_id))}</td>
                    <td>
                      <strong>{m.direction}</strong>
                    </td>
                    <td>{m.direction === 'team' ? m.author_email ?? '—' : '—'}</td>
                    <td style={{ maxWidth: 360, overflowWrap: 'anywhere' }}>{m.body}</td>
                    <td>{m.status ?? '—'}</td>
                    <td>{m.direction === 'team' ? fmtTs(m.read_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && section === 'suppressions' && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Suppression rows (verbatim)</th>
                </tr>
              </thead>
              <tbody>
                {data.suppressions.length === 0 && (
                  <tr>
                    <td className="admin-empty">No suppressions in scope.</td>
                  </tr>
                )}
                {data.suppressions.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <pre
                        style={{
                          margin: 0,
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {JSON.stringify(s, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
}
